import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { User, InstructorStudentAssignment } from '../types';
import Layout from '../components/Layout/Layout';

const AssignmentsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [assignments, setAssignments] = useState<InstructorStudentAssignment[]>([]);
  const [selectedInstructor, setSelectedInstructor] = useState<string>('');
  const [selectedStudent, setSelectedStudent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedStudentForCases, setSelectedStudentForCases] = useState<string | null>(null);
  const [studentCases, setStudentCases] = useState<any[]>([]);

  const isAdmin = user?.role === 'ADMIN';
  const isInstructor = user?.role === 'INSTRUCTOR';

  useEffect(() => {
    if (isAdmin || isInstructor) {
      loadData();
    }
  }, [isAdmin, isInstructor]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, assignmentsData] = await Promise.all([
        api.getAllUsers(),
        isAdmin ? api.getAllAssignments() : api.getInstructorStudents(user!.id)
      ]);
      setUsers(usersData);
      setAssignments(assignmentsData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedInstructor || !selectedStudent) {
      setError('Please select both instructor and student');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      await api.assignStudentToInstructor(selectedInstructor, selectedStudent);
      setSuccess('Student assigned successfully!');
      setSelectedInstructor('');
      setSelectedStudent('');
      
      // Reload assignments
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to assign student');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (assignmentId: string) => {
    if (!window.confirm('Are you sure you want to remove this assignment?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      await api.removeStudentFromInstructor(assignmentId);
      setSuccess('Assignment removed successfully!');
      
      // Reload assignments
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudentCases = async (studentId: string, studentName: string) => {
    try {
      setLoading(true);
      setError(null);
      const cases = await api.getStudentCases(studentId);
      setStudentCases(cases);
      setSelectedStudentForCases(studentName);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load student cases');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCasesModal = () => {
    setSelectedStudentForCases(null);
    setStudentCases([]);
  };

  const instructors = users.filter(u => u.role === 'INSTRUCTOR' || u.role === 'ADMIN');
  const students = users.filter(u => u.role === 'STUDENT');

  // For instructors, pre-select themselves
  useEffect(() => {
    if (isInstructor && !isAdmin && user) {
      setSelectedInstructor(user.id);
    }
  }, [isInstructor, isAdmin, user]);

  if (!isAdmin && !isInstructor) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">You don't have permission to access this page.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Student-Instructor Assignments
        </h1>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800">{success}</p>
          </div>
        )}

        {/* Assignment Form */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Assign Student to Instructor</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instructor
              </label>
              <select
                value={selectedInstructor}
                onChange={(e) => setSelectedInstructor(e.target.value)}
                disabled={isInstructor && !isAdmin}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">Select Instructor</option>
                {instructors.map(instructor => (
                  <option key={instructor.id} value={instructor.id}>
                    {instructor.name} ({instructor.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Student
              </label>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Student</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.name} ({student.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleAssign}
                disabled={loading || !selectedInstructor || !selectedStudent}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>

        {/* Assignments List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">Current Assignments</h2>
          </div>
          
          {loading && assignments.length === 0 ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : assignments.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No assignments yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Instructor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned By
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assignments.map((assignment) => (
                    <tr key={assignment.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {assignment.instructor?.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {assignment.instructor?.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {assignment.student?.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {assignment.student?.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(assignment.assignedAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {assignment.assignedByUser?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                        <button
                          onClick={() => handleViewStudentCases(assignment.studentId, assignment.student?.name || 'Student')}
                          disabled={loading}
                          className="text-blue-600 hover:text-blue-900 disabled:text-gray-400"
                        >
                          View Cases
                        </button>
                        <button
                          onClick={() => handleRemove(assignment.id)}
                          disabled={loading}
                          className="text-red-600 hover:text-red-900 disabled:text-gray-400"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Student Cases Modal */}
        {selectedStudentForCases && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold">
                  Cases for {selectedStudentForCases}
                </h2>
                <button
                  onClick={handleCloseCasesModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto">
                {studentCases.length === 0 ? (
                  <p className="text-center text-gray-500">This student has no cases yet.</p>
                ) : (
                  <div className="space-y-4">
                    {studentCases.map((caseInstance) => (
                      <div
                        key={caseInstance.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors"
                        onClick={() => {
                          navigate(`/cases/${caseInstance.instanceId}`);
                          handleCloseCasesModal();
                        }}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {caseInstance.title}
                          </h3>
                          <span className="text-sm text-gray-500">
                            {caseInstance.masterCase?.caseId || 'Custom'}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {caseInstance.scenario}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center space-x-4">
                            <span>
                              Started: {new Date(caseInstance.startedAt).toLocaleDateString()}
                            </span>
                            <span>
                              Last accessed: {new Date(caseInstance.lastAccessedAt).toLocaleDateString()}
                            </span>
                          </div>
                          {caseInstance.userDialogue && (
                            <span className="text-green-600 font-medium">
                              Has dialogue
                            </span>
                          )}
                        </div>
                        
                        {caseInstance.versions && caseInstance.versions.length > 0 && (
                          <div className="mt-2 text-xs text-gray-500">
                            {caseInstance.versions.length} version{caseInstance.versions.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AssignmentsPage;

